import { useEffect, useRef, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Settings → Templates (docs/16 §E). CRUD over the existing templates table;
// preview runs the live render_template() RPC so the admin sees resolved output.
// Channels: sms / email / task (email adds a subject). Does NOT change
// render_template's contract — it consumes the same tokens the engine supports.

const CHANNELS = ["sms", "email", "task"] as const;
type Channel = (typeof CHANNELS)[number];

// The merge fields render_template() actually resolves today (see
// supabase migration 0017 render_template). Surfaced verbatim — not invented.
const MERGE_FIELDS = [
  { token: "{{first_name}}", desc: "Lead's first name (→ 'there' if unknown)" },
  { token: "{{property_address}}", desc: "Property street address" },
  { token: "{{city}}", desc: "Property city" },
];

type Template = {
  id: string;
  channel: Channel;
  key: string;
  name: string;
  subject: string | null;
  body: string;
};
type Draft = { id?: string; channel: Channel; name: string; subject: string; body: string };
const EMPTY: Draft = { channel: "sms", name: "", subject: "", body: "" };

const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const TemplatesPage = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<Template[]>([]);
  const [filter, setFilter] = useState<"all" | Channel>("all");
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [preview, setPreview] = useState<string | null>(null);
  const [sampleLead, setSampleLead] = useState<{ id: string; label: string } | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = async () => {
    const { data } = await getSupabaseClient().from("templates").select("*").order("channel").order("name");
    setRows((data as Template[]) ?? []);
  };
  useEffect(() => {
    load();
    // grab any one lead to preview merge-field resolution against
    getSupabaseClient().from("leads").select("id").limit(1).maybeSingle().then(({ data }) => {
      if (data) setSampleLead({ id: (data as any).id, label: "a sample lead" });
    });
  }, []);

  const edit = (t: Template) => {
    setDraft({ id: t.id, channel: t.channel, name: t.name, subject: t.subject ?? "", body: t.body });
    setPreview(null);
  };
  const isEmail = draft.channel === "email";

  const insertToken = (token: string) => {
    const el = bodyRef.current;
    if (!el) { setDraft((d) => ({ ...d, body: d.body + token })); return; }
    const start = el.selectionStart ?? draft.body.length;
    const end = el.selectionEnd ?? draft.body.length;
    const next = draft.body.slice(0, start) + token + draft.body.slice(end);
    setDraft((d) => ({ ...d, body: next }));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const runPreview = async () => {
    if (!sampleLead) { notify("No lead available to preview against — add a lead first.", { type: "warning" }); return; }
    const { data, error } = await getSupabaseClient().rpc("render_template", { p_body: draft.body, p_lead_id: sampleLead.id });
    if (error) { notify(`Preview failed: ${error.message}`, { type: "error" }); return; }
    setPreview((data as string) ?? "");
  };

  const save = async () => {
    if (!draft.name.trim() || !draft.body.trim()) { notify("Name and body are required.", { type: "warning" }); return; }
    const sb = getSupabaseClient();
    const payload = {
      channel: draft.channel,
      name: draft.name.trim(),
      subject: isEmail ? (draft.subject.trim() || null) : null,
      body: draft.body,
    };
    if (draft.id) {
      const { error } = await sb.from("templates").update(payload).eq("id", draft.id);
      if (error) { notify(`Save failed: ${error.message}`, { type: "error" }); return; }
      notify("Template saved", { type: "success" });
    } else {
      // derive a unique key from the name (templates.key is unique).
      const base = slug(draft.name) || "template";
      const existing = new Set(rows.map((r) => r.key));
      let key = base; let i = 2;
      while (existing.has(key)) key = `${base}_${i++}`;
      const { error } = await sb.from("templates").insert({ ...payload, key });
      if (error) { notify(`Create failed: ${error.message}`, { type: "error" }); return; }
      notify("Template created", { type: "success" });
    }
    setDraft(EMPTY); setPreview(null);
    load();
  };

  const del = async (t: Template) => {
    if (!window.confirm(`Delete template "${t.name}"? Automations referencing "${t.key}" would no longer resolve.`)) return;
    const { error } = await getSupabaseClient().from("templates").delete().eq("id", t.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" });
    else { notify("Template deleted", { type: "success" }); if (draft.id === t.id) setDraft(EMPTY); load(); }
  };

  const visible = rows.filter((r) => filter === "all" || r.channel === filter);

  return (
    <div className="p-2 max-w-5xl">
      <h1 className="text-xl font-semibold">Templates</h1>
      <div className="text-sm text-muted-foreground mb-4">
        SMS, email, and task templates with merge fields. Preview resolves the tokens via the live render engine.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">Templates</CardTitle>
            <div className="flex items-center gap-2">
              <select className="border rounded px-2 py-1 text-sm bg-background" value={filter} onChange={(e) => setFilter(e.target.value as any)}>
                <option value="all">All types</option>
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={() => { setDraft(EMPTY); setPreview(null); }}>New</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            {visible.map((t) => (
              <div key={t.id} className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm border-b last:border-0 cursor-pointer hover:bg-muted/50 ${draft.id === t.id ? "bg-muted" : ""}`} onClick={() => edit(t)}>
                <Badge variant="outline" className="uppercase text-[10px]">{t.channel}</Badge>
                <span className="font-medium">{t.name}</span>
                <span className="text-xs text-muted-foreground truncate">{t.body}</span>
                <Button variant="ghost" size="sm" className="ml-auto shrink-0" onClick={(e) => { e.stopPropagation(); del(t); }}>Delete</Button>
              </div>
            ))}
            {visible.length === 0 && <div className="text-sm text-muted-foreground py-2">No templates{filter !== "all" ? ` of type ${filter}` : ""} yet.</div>}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card>
          <CardHeader><CardTitle className="text-base">{draft.id ? "Edit template" : "New template"}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-3">
              <label className="flex flex-col gap-1 flex-1">
                <span className="text-xs text-muted-foreground">Type</span>
                <select className="border rounded px-2 py-2 text-sm bg-background" value={draft.channel} onChange={(e) => setDraft((d) => ({ ...d, channel: e.target.value as Channel }))}>
                  {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 flex-[2]">
                <span className="text-xs text-muted-foreground">Name</span>
                <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. New-lead speed-to-lead" />
              </label>
            </div>

            {isEmail && (
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Subject</span>
                <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} placeholder="Email subject line" />
              </label>
            )}

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Body</span>
                <div className="flex flex-wrap gap-1">
                  {MERGE_FIELDS.map((m) => (
                    <button key={m.token} type="button" title={m.desc} onClick={() => insertToken(m.token)} className="text-[11px] rounded border px-1.5 py-0.5 hover:bg-muted font-mono">{m.token}</button>
                  ))}
                </div>
              </div>
              <Textarea ref={bodyRef} rows={5} value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} placeholder={draft.channel === "task" ? "Task description…" : "Message body… click a merge field to insert it"} />
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={save}>{draft.id ? "Save" : "Create"}</Button>
              <Button variant="outline" onClick={runPreview} disabled={!draft.body.trim()}>Preview</Button>
              {draft.id && <Button variant="ghost" onClick={() => { setDraft(EMPTY); setPreview(null); }}>Cancel</Button>}
            </div>

            {preview != null && (
              <div className="rounded border bg-muted/40 p-2">
                <div className="text-xs text-muted-foreground mb-1">Preview (resolved against {sampleLead?.label ?? "no lead"})</div>
                <div className="text-sm whitespace-pre-wrap">{preview || <span className="text-muted-foreground">(empty)</span>}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

TemplatesPage.path = "/settings/templates";
export default TemplatesPage;
